import React, { useState } from 'react';
import { X, Save, Loader } from 'lucide-react';
import { createVectorData, updateVectorData } from '../services/vectorDataService';

const TYPES = [
  { id: 'about', label: 'Profile' },
  { id: 'services', label: 'Services' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'skills', label: 'Skills' },
  { id: 'past_projects', label: 'Past Projects' },
  { id: 'proposal', label: 'Proposal' },
];

const AIKnowledgeForm = ({ item, defaultType = 'about', onClose, onSaved }) => {
  const isEditing = !!item;
  const itemType = isEditing ? (item.type || item.metadata?.type) : defaultType;

  const [type, setType] = useState(itemType);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    text: item?.text || '',
    name: item?.metadata?.name || '',
    project_name: item?.metadata?.project_name || '',
    skill_name: item?.metadata?.skill_name || '',
    jobTitle: item?.metadata?.jobTitle || '',
    jobDescription: item?.metadata?.jobDescription || '',
    coverLetter: item?.metadata?.coverLetter || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (type === 'proposal') {
      if (!formData.jobTitle) {
        alert('Job Title is required');
        return;
      }
      if (!formData.coverLetter) {
        alert('Cover Letter is required');
        return;
      }
    } else if (!formData.text?.trim()) {
      alert('Content is required');
      return;
    }

    setSaving(true);

    try {
      const payload = { text: formData.text };

      if (type === 'skills' && formData.skill_name) payload.skill_name = formData.skill_name;
      if (type === 'past_projects' && formData.project_name) payload.project_name = formData.project_name;
      if (formData.name) payload.name = formData.name;

      if (type === 'proposal') {
        payload.jobTitle = formData.jobTitle;
        payload.jobDescription = formData.jobDescription || '';
        payload.coverLetter = formData.coverLetter;
        payload.text = formData.jobTitle + (formData.jobDescription ? `\n${formData.jobDescription}` : '');
      }

      if (isEditing) {
        await updateVectorData(itemType, item.vectorId, payload);
      } else {
        await createVectorData(type, payload);
      }
      onSaved();
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
      <div className="relative bg-white w-full max-w-2xl mx-auto rounded-lg shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {isEditing ? 'Edit Entry' : 'Add Entry'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" type="button">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type selector (only for new entries) */}
          {!isEditing && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          )}

          {type === 'proposal' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Job Title *</label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => set('jobTitle', e.target.value)}
                  required
                  className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Job Description</label>
                <textarea
                  value={formData.jobDescription}
                  onChange={(e) => set('jobDescription', e.target.value)}
                  rows={4}
                  className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cover Letter *</label>
                <textarea
                  value={formData.coverLetter}
                  onChange={(e) => set('coverLetter', e.target.value)}
                  required
                  rows={10}
                  className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </>
          ) : (
            <>
              {['about', 'services', 'testimonials'].includes(type) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => set('name', e.target.value)}
                    className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional name/title"
                  />
                </div>
              )}
              {type === 'skills' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Skill Name *</label>
                  <input
                    type="text"
                    value={formData.skill_name}
                    onChange={(e) => set('skill_name', e.target.value)}
                    required
                    className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., React, Node.js"
                  />
                </div>
              )}
              {type === 'past_projects' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Project Name *</label>
                  <input
                    type="text"
                    value={formData.project_name}
                    onChange={(e) => set('project_name', e.target.value)}
                    required
                    className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., E-commerce Platform"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Content *</label>
                <textarea
                  value={formData.text}
                  onChange={(e) => set('text', e.target.value)}
                  required
                  rows={6}
                  className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter content..."
                />
              </div>
            </>
          )}

          <div className="flex justify-end space-x-2 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
            >
              {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIKnowledgeForm;
